import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  Newspaper,
  Calendar,
  Search,
  X,
  ChevronDown,
  BookOpen,
  Sparkles,
  ArrowRight,
  User,
} from 'lucide-react';

import { useRegionScope } from '@/hooks/useRegionScope';
import { PublicShell } from '@/components/public-shell';
import { fetchJson } from '@/lib/queryClient';
import { ComingSoonBanner } from '@/components/coming-soon-banner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getLatestNewsletterIssues, getNewsletterSeriesName } from '@/lib/newsletter-series';

interface BlogPostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImageUrl: string | null;
  publishedAt: string | null;
  authorFirstName: string | null;
  authorLastName: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  isPinned: boolean;
  tags: string[];
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

const PAGE_SIZE = 9;
const NEWSLETTER_CATEGORY_NAME = 'Newsletter';
const RECENT_DAYS = 7;

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

function isRecent(publishedAt: string | null): boolean {
  if (!publishedAt) return false;
  return Date.now() - new Date(publishedAt).getTime() < RECENT_DAYS * 24 * 60 * 60 * 1000;
}

function isNew(publishedAt: string | null): boolean {
  return isRecent(publishedAt);
}

/** Prominent newsletter card — sits above the article feed */
function NewsletterCard({ post, basePath }: { post: BlogPostSummary; basePath: string }) {
  const fresh = isNew(post.publishedAt);
  const seriesName = getNewsletterSeriesName(post);
  return (
    <Link href={`${basePath}/updates/${post.slug}`}>
      <div className="rounded-xl border-2 border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40 transition-all cursor-pointer group">
        <div className="p-5 flex items-center gap-5">
          <div className="shrink-0 flex items-center justify-center h-14 w-14 rounded-xl bg-primary/15 text-primary group-hover:bg-primary/25 transition-colors">
            <BookOpen className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wide text-primary">
                Latest Issue
              </span>
              <span className="text-xs font-semibold text-foreground">{seriesName}</span>
              {fresh && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                  <Sparkles className="h-2.5 w-2.5" />
                  Just published
                </span>
              )}
              {post.publishedAt && (
                <span className="text-xs text-muted-foreground">
                  · {formatDateShort(post.publishedAt)}
                </span>
              )}
            </div>
            <p className="font-bold text-base leading-snug group-hover:text-primary transition-colors">
              {post.title}
            </p>
            {post.excerpt && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{post.excerpt}</p>
            )}
          </div>
          <div className="shrink-0 hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary">
            Read now
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}

/** Enlarged horizontal card for Recent Updates section */
function RecentPostCard({ post, basePath }: { post: BlogPostSummary; basePath: string }) {
  const fresh = isNew(post.publishedAt);
  return (
    <Link href={`${basePath}/updates/${post.slug}`}>
      <Card className="overflow-hidden hover:border-primary/50 transition-colors cursor-pointer group">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-2/5 h-52 sm:h-auto overflow-hidden shrink-0 bg-muted flex items-center justify-center">
            {post.featuredImageUrl ? (
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <Newspaper className="h-12 w-12 text-muted-foreground/30" />
            )}
          </div>
          <CardContent className="p-5 sm:p-7 flex flex-col justify-between flex-1">
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {post.categoryName && (
                  <Badge variant="secondary" className="text-xs">
                    {post.categoryName}
                  </Badge>
                )}
                {fresh && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                    <Sparkles className="h-2.5 w-2.5" />
                    New
                  </span>
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold leading-snug group-hover:text-primary transition-colors">
                {post.title}
              </h2>
              {post.excerpt && <p className="text-muted-foreground line-clamp-3">{post.excerpt}</p>}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(post.publishedAt)}
                </span>
                {(post.authorFirstName || post.authorLastName) && (
                  <span className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    {post.authorFirstName} {post.authorLastName}
                  </span>
                )}
              </div>
              <span className="text-sm font-medium text-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Read more <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </CardContent>
        </div>
      </Card>
    </Link>
  );
}

export default function RegionUpdatesBrowse() {
  const { regionSlug, localitySlug } = useParams<{ regionSlug: string; localitySlug?: string }>();
  const { region, locality, isLoading: geoLoading } = useRegionScope(regionSlug, localitySlug);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const prevFilters = useRef({ debouncedSearch, selectedCategory });
  useEffect(() => {
    if (
      prevFilters.current.debouncedSearch !== debouncedSearch ||
      prevFilters.current.selectedCategory !== selectedCategory
    ) {
      prevFilters.current = { debouncedSearch, selectedCategory };
      setDisplayLimit(PAGE_SIZE);
    }
  }, [debouncedSearch, selectedCategory]);

  const regionId = region?.id;
  const localityId = locality?.id;

  const { data: categories = [] } = useQuery<BlogCategory[]>({
    queryKey: ['/api/blog/categories'],
    queryFn: () => fetchJson<BlogCategory[]>('/api/blog/categories'),
  });

  const newsletterCatId = categories.find(
    c => c.name.toLowerCase() === NEWSLETTER_CATEGORY_NAME.toLowerCase()
  )?.id;

  // Newsletter: strict locality filter when locality selected, all newsletters at region level.
  // The UI collapses historical issues into one visible latest issue per newsletter series.
  const { data: newsletterPosts = [] } = useQuery<BlogPostSummary[]>({
    queryKey: ['/api/blog/newsletter', regionId, localityId, newsletterCatId],
    queryFn: async () => {
      if (!regionId || !newsletterCatId) return [];
      const params = new URLSearchParams({
        categoryId: newsletterCatId,
        limit: localityId ? '40' : '80',
        offset: '0',
      });
      if (localityId) {
        params.set('localityId', localityId);
      } else {
        params.set('regionId', regionId);
      }
      return fetchJson<BlogPostSummary[]>(`/api/blog?${params}`);
    },
    enabled: !!regionId && !!newsletterCatId,
  });

  // Main posts: locality-scoped (locality posts + region-wide) when locality selected
  const { data: posts = [], isLoading: postsLoading } = useQuery<BlogPostSummary[]>({
    queryKey: ['/api/blog', regionId, localityId, debouncedSearch, selectedCategory, displayLimit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (regionId) params.set('regionId', regionId);
      if (localityId) params.set('localityId', localityId);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedCategory) params.set('categoryId', selectedCategory);
      params.set('limit', String(displayLimit + 1));
      params.set('offset', '0');
      return fetchJson<BlogPostSummary[]>(`/api/blog?${params}`);
    },
    enabled: !!regionId,
  });

  const hasMore = posts.length > displayLimit;
  const displayedPosts = posts.slice(0, displayLimit);

  // Exclude newsletters from the article feed (they have their own dedicated card)
  const nonNewsletterPosts = displayedPosts.filter(p => p.categorySlug !== 'newsletter');
  const latestNewsletterPosts = getLatestNewsletterIssues(newsletterPosts);

  const isFiltering = !!(debouncedSearch || selectedCategory);

  // Recent Updates: posts from last 7 days + always the most recent post
  const mostRecentPost = nonNewsletterPosts[0] ?? null;
  const recentPosts = !isFiltering
    ? nonNewsletterPosts.filter(p => p.id === mostRecentPost?.id || isRecent(p.publishedAt))
    : [];
  const recentPostIds = new Set(recentPosts.map(p => p.id));

  // More Updates: everything outside the recent section (or all posts when filtering)
  const olderPosts = isFiltering
    ? nonNewsletterPosts
    : nonNewsletterPosts.filter(p => !recentPostIds.has(p.id));

  const displayName = locality?.name ?? region?.name ?? 'this area';
  const isComingSoon = locality?.slug === 'bloomington';
  const basePath = locality ? `/regions/${regionSlug}/${localitySlug}` : `/regions/${regionSlug}`;

  const isLoading = geoLoading || (postsLoading && displayedPosts.length === 0);

  const hasAnyContent = recentPosts.length > 0 || olderPosts.length > 0;

  return (
    <PublicShell routeKey="updates">
      <div className="mx-auto max-w-[var(--page-max-width)] px-4 py-8 sm:px-6">
        <div className="mb-6">
          <p className="editorial-kicker">News &amp; announcements</p>
          <h1 className="text-3xl font-bold">Updates in {displayName}</h1>
          <p className="text-muted-foreground mt-1">
            Stories, events, and community news from {displayName}.
          </p>
        </div>

        {isComingSoon ? (
          <div className="mb-8">
            <ComingSoonBanner kind="updates" localityName={displayName} />
          </div>
        ) : null}

        {/* Community Newsletter section */}
        {latestNewsletterPosts.length > 0 && (
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              {latestNewsletterPosts.length === 1
                ? 'Community Newsletter'
                : 'Community Newsletters'}
            </p>
            <div className="space-y-3">
              {latestNewsletterPosts.map(post => (
                <NewsletterCard key={post.id} post={post} basePath={basePath} />
              ))}
            </div>
          </div>
        )}

        {/* Search + category filters */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search posts…"
                className="pl-9 pr-8"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {isFiltering && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setSelectedCategory('');
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {categories.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setSelectedCategory('')}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors border ${
                  selectedCategory === ''
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors border ${
                    selectedCategory === cat.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-48 rounded-xl animate-pulse bg-muted" />
            ))}
          </div>
        ) : !hasAnyContent && isComingSoon && !isFiltering ? null : !hasAnyContent ? (
          <div className="py-16 text-center text-muted-foreground space-y-2">
            <Newspaper className="h-10 w-10 mx-auto opacity-30" />
            {isFiltering ? (
              <>
                <p className="text-lg font-medium">No posts match your filters</p>
                <p className="text-sm">Try a different search term or category.</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('');
                    setSelectedCategory('');
                  }}
                >
                  Clear filters
                </Button>
              </>
            ) : (
              <>
                <p className="text-lg font-medium">No updates yet for {displayName}</p>
                <p className="text-sm">Check back soon — we'll share news and events here.</p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Recent Updates section */}
            {!isFiltering && recentPosts.length > 0 && (
              <div className="mb-10">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                  Recent Updates
                </p>
                <div className="space-y-4">
                  {recentPosts.map(post => (
                    <RecentPostCard key={post.id} post={post} basePath={basePath} />
                  ))}
                </div>
              </div>
            )}

            {/* More Updates section */}
            {olderPosts.length > 0 && (
              <>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                  {isFiltering ? 'Results' : 'More Updates'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {olderPosts.map(post => (
                    <Link key={post.id} href={`${basePath}/updates/${post.slug}`}>
                      <Card className="overflow-hidden hover:border-primary/50 transition-colors cursor-pointer h-full flex flex-col group">
                        {post.featuredImageUrl ? (
                          <div className="h-40 overflow-hidden shrink-0">
                            <img
                              src={post.featuredImageUrl}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        ) : (
                          <div className="h-40 bg-muted flex items-center justify-center shrink-0">
                            <Newspaper className="h-8 w-8 text-muted-foreground/40" />
                          </div>
                        )}
                        <CardContent className="p-4 flex flex-col gap-2 flex-1">
                          {post.categoryName && (
                            <Badge variant="secondary" className="w-fit text-xs">
                              {post.categoryName}
                            </Badge>
                          )}
                          <p className="font-semibold line-clamp-2 leading-snug group-hover:text-primary transition-colors">
                            {post.title}
                          </p>
                          {post.excerpt && (
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {post.excerpt}
                            </p>
                          )}
                          <div className="mt-auto flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(post.publishedAt)}</span>
                            {(post.authorFirstName || post.authorLastName) && (
                              <>
                                <span>·</span>
                                <span>
                                  {post.authorFirstName} {post.authorLastName}
                                </span>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </>
            )}

            {hasMore && (
              <div className="mt-10 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => setDisplayLimit(l => l + PAGE_SIZE)}
                  disabled={postsLoading}
                >
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicShell>
  );
}
